import {
  Button,
  Tooltip,
  MenuItem,
  Select,
  Box,
  Alert,
  Typography,
} from '@mui/material'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import type { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { DataGrid } from '@mui/x-data-grid'
import flatMap from 'lodash/flatMap'
import groupBy from 'lodash/groupBy'
import type { Dispatch } from 'react'
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import * as semver from 'semver'
import { useFetchFotaDevicePage } from '@/api/fota'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import type { UpdateReducerAction } from '@/pages/fota/create/reducers'
import { UpdatesContext } from '@/pages/fota/create/reducers'
import type { FotaDeviceView } from '@/pages/fota/fota'
import types from '@/pages/Device/DeviceType'
import { IsOnline, Uid } from '@/pages/utils/formatter'
import { useFotaCreateFilters } from '@/hooks/useTransalationForFilter'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import { FilterDrawer } from '@/components/FilterDrawer'
import { useFilters } from '@/hooks/useFilters'
import { useSearchParams } from 'react-router-dom'
import { SelectedFilters } from '@/models/filters'

type Props = {
  value: FotaDeviceView
  onRowSelect: (deviceId: number | string) => void
  onRowDeselect: (deviceId: number | string) => void
  updatesState: Record<number | string, (number | string)[]>
  updatesDispatch: Dispatch<UpdateReducerAction>
  userManuallySelected?: React.RefObject<Set<string | number>>
  componentId?: string | number
}

const UpdateSelect = ({
  value,
  onRowSelect,
  onRowDeselect,
  updatesState,
  updatesDispatch,
  userManuallySelected,
  componentId,
}: Props) => {
  const { t } = useTranslation(['di', 'app'])
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const anyUpdateSelected = useMemo(() => {
    const selectedUpdates = updatesState[value.uid] || []
    return selectedUpdates.length > 0 && selectedUpdates[0] !== ''
  }, [updatesState, value.uid])

  const selectedUpdateId = useMemo(() => {
    const selectedUpdates = updatesState[value.uid] || []
    return selectedUpdates.length > 0 ? selectedUpdates[0] : ''
  }, [updatesState, value.uid])

  const hasAnyActiveJob = useMemo(() => {
    return value.availableUpdates.some((update) => !!update.jobId)
  }, [value.availableUpdates])

  const handleChange = useCallback(
    (event: { target: { value: (string | number)[] } }) => {
      setTooltipOpen(false)
      const selectedValues = event.target.value

      // Get the most recently selected value (the last one in the array)
      // or use the first non-empty value if available
      let selectedId = ''
      if (selectedValues.length > 0) {
        // Look for any non-empty, non-type values (actual update IDs)
        const updateIds = selectedValues.filter((val) => {
          // Check if this is an actual update ID (not a type header)
          return (
            val !== '' &&
            value.availableUpdates.some((update) => update.id === val)
          )
        })
        if (updateIds.length > 0) {
          // Use the last selected update ID (most recent selection)
          selectedId = updateIds.at(-1)
        }
      }

      if (selectedId) {
        // Set just this one update
        if (userManuallySelected?.current) {
          userManuallySelected.current.add(value.uid)
        }
        updatesDispatch({
          action: 'set',
          updates: [selectedId],
          device: value.uid,
        })
        onRowSelect(value.uid)
      } else {
        updatesDispatch({
          action: 'unset',
          device: value.uid,
        })
        if (userManuallySelected?.current) {
          userManuallySelected.current.delete(value.uid)
        }
        onRowDeselect(value.uid)
      }
    },
    [
      updatesDispatch,
      value.uid,
      value.availableUpdates,
      onRowSelect,
      onRowDeselect,
      userManuallySelected,
    ],
  )

  const getComponentTypeText = useCallback(
    (type: string) => {
      // First try the standard translation
      const translated = t(`di:fota.componentsEnum.${type}`)

      // If it failed (returns the key itself), use fallbacks
      if (
        translated === `di:fota.componentsEnum.${type}` ||
        translated === `fota.componentsEnum.${type}`
      ) {
        const fallbacks = {
          nmdi: 'NMDI',
          mainFirmware: 'Main Firmware',
        }
        return fallbacks[type] ?? type
      }
      return translated
    },
    [t],
  )

  const renderSelectValue = useCallback(() => {
    if (value.availableUpdates.length === 0) {
      return t('di:fota.noUpdates')
    }
    const selectedId = selectedUpdateId
    if (!selectedId) {
      return t('di:fota.selectUpdates')
    }
    const update = value.availableUpdates.find((u) => u.id === selectedId)
    if (update) {
      return getComponentTypeText(update.type) + ' (' + update.version + ')'
    }
    return t('di:fota.selectUpdates')
  }, [selectedUpdateId, value.availableUpdates, t, getComponentTypeText])

  const menuItems = useMemo(() => {
    // Sort updates by version within each type (highest version first)
    const sortedUpdates = [...value.availableUpdates].sort((a, b) => {
      // First sort by type alphabetically
      const typeCompare = a.type.localeCompare(b.type)
      if (typeCompare !== 0) return typeCompare

      // Then sort by semantic versioning (highest first)
      return semver.gt(a.version, b.version) ? -1 : 1
    })
    // Group by type after sorting
    const groupedByType = groupBy(sortedUpdates, (u) => u.type)
    // Get the types in alphabetical order
    const sortedTypes = Object.keys(groupedByType).sort((a, b) =>
      a.localeCompare(b),
    )

    // Create the menu items with sorted types and versions
    return flatMap(sortedTypes, (type) => {
      const updates = groupedByType[type]
      // Sort updates by version (lowest to highest)
      const sortedByVersion = [...updates].sort((a, b) =>
        semver.gt(a.version, b.version) ? 1 : -1,
      )

      return [
        <MenuItem disabled={true} value={type} key={type}>
          <Box sx={{ fontWeight: 'bold' }}>{getComponentTypeText(type)}</Box>
        </MenuItem>,
        ...sortedByVersion.map((update) => (
          <MenuItem
            key={update.id}
            value={update.id}
            disabled={
              !update.selectable ||
              hasAnyActiveJob ||
              (anyUpdateSelected && update.id !== selectedUpdateId)
            }
            title={getComponentTypeText(type)}
          >
            <Box sx={{ paddingLeft: 0, textAlign: 'left' }}>
              {update.version}{' '}
              {update.jobId ? `(${t('di:fota.hasActiveJob')})` : ''}
            </Box>
          </MenuItem>
        )),
      ]
    })
  }, [
    value.availableUpdates,
    t,
    hasAnyActiveJob,
    anyUpdateSelected,
    selectedUpdateId,
    getComponentTypeText,
  ])

  return (
    <Tooltip
      open={tooltipOpen}
      title={
        value.selectableReason
          ? t(`di:fota.reasonEnum.${value.selectableReason}`)
          : ''
      }
      placement="top"
    >
      <Select
        className="updateSelect"
        data-testid={`update-select-${value.uid}`}
        id="firmaware-updates"
        label="firmaware-updates"
        disabled={value.availableUpdates.length === 0}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onChange={handleChange}
        renderValue={renderSelectValue}
        onClick={(e) => {
          setTooltipOpen(false)
          e.stopPropagation()
        }}
        style={{ color: value.selectable ? 'inherit' : 'grey' }}
        value={updatesState[value.uid] ?? ['']}
        fullWidth={true}
        displayEmpty
        multiple
        data-row-id={componentId}
      >
        {menuItems}
      </Select>
    </Tooltip>
  )
}

const MemoizedUpdateSelect = React.memo(UpdateSelect)
type SelectProps = {
  setActiveStep: (step: number) => void
}

const parseFiltersFromUrl = (
  searchParams: URLSearchParams,
): SelectedFilters => {
  const filters: SelectedFilters = {}
  const reservedParams = new Set(['p', 'size', 'q', 'sort', 'order'])

  for (const key of searchParams.keys()) {
    if (!reservedParams.has(key)) {
      if (!filters[key]) {
        filters[key] = {}
      }
      const allValues = searchParams.getAll(key)
      for (const val of allValues) {
        filters[key][val] = val
      }
    }
  }

  return filters
}

const SelectUpdatesStep = ({ setActiveStep }: SelectProps) => {
  const { t } = useTranslation(['di', 'app'])
  const filterConfig = useFotaCreateFilters()
  const { fetchFotaDevicePage } = useFetchFotaDevicePage()

  const [searchParams, setSearchParams] = useSearchParams()

  const getUrlState = useCallback(() => {
    const p = Math.max(0, Number(searchParams.get('p') ?? 1) - 1)
    const size = Number(searchParams.get('size')) || 10
    const q = searchParams.get('q') ?? ''
    const sort = searchParams.get('sort') ?? ''
    const order = searchParams.get('order') ?? ''

    const sortModel: GridSortModel = sort
      ? [{ field: sort, sort: order === 'desc' ? 'desc' : 'asc' }]
      : []

    const filters = parseFiltersFromUrl(searchParams)

    return { p, size, q, sortModel, filters }
  }, [searchParams])

  const urlState = getUrlState()

  // State management
  const [deviceData, setDeviceData] = useState([])
  const [countOfAvailableUpdates, setCountOfAvailableUpdates] = useState(0)
  const [pageIndex, setPageIndex] = useState(urlState.p)
  const [pageSize, setPageSize] = useState(urlState.size)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [open, setOpen] = useState(false)
  const [localSearchTerm, setLocalSearchTerm] = useState<string>(urlState.q)
  const localFilterHooks = useFilters()
  const [error, setError] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>(urlState.sortModel)

  // Refs
  const skipAutoSelection = useRef(new Set())
  const userManuallySelected = useRef(new Set())
  const abortController = useRef(new AbortController())
  const mountedRef = useRef(false)
  const isInitialMount = useRef(true)
  const filtersInitialized = useRef(false)
  const initialFilters = useRef(urlState.filters)
  const searchDebounceTimer = useRef<NodeJS.Timeout>()

  const { state: updatesState, dispatch: updatesDispatch } =
    useContext(UpdatesContext)

  const handleConfirm = useCallback(() => {
    setOpen(false)
    setActiveStep(1)
  }, [setActiveStep])

  const handleCancel = useCallback(() => {
    setOpen(false)
  }, [])

  const loadData = useCallback(
    async (
      page: number,
      size: number,
      search: string,
      sort: GridSortModel,
      filters: Record<string, unknown>,
    ) => {
      if (!mountedRef.current) return

      abortController.current.abort()
      abortController.current = new AbortController()

      setIsLoading(true)
      setError(null)

      try {
        const sortBy =
          sort.length > 0
            ? [
                {
                  id: sort[0].field,
                  desc: sort[0].sort === 'desc',
                },
              ]
            : []

        const result = await fetchFotaDevicePage(
          {
            pageIndex: page,
            pageSize: size,
            sortBy,
            globalFilter: search,
            selectedFilters: filters,
          },
          { signal: abortController.current.signal },
        )

        const transformedData = result.data?.length
          ? result.data.map((device) => ({
              ...device,
              id: device.uid,
            }))
          : []

        setDeviceData(transformedData)
        setCountOfAvailableUpdates(result.rowCount ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching data:', error)
          setError(error.message ?? 'Failed to load data')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [fetchFotaDevicePage],
  )

  // Initialize filters from URL SYNCHRONOUSLY before mount
  if (
    !filtersInitialized.current &&
    Object.keys(initialFilters.current).length > 0
  ) {
    for (const [groupId, options] of Object.entries(initialFilters.current)) {
      for (const [optionKey, optionLabel] of Object.entries(options)) {
        localFilterHooks.handleFilterChange(
          groupId,
          optionKey,
          optionLabel,
          true,
        )
      }
    }
    filtersInitialized.current = true
  }

  // Initial mount - use URL filters
  useEffect(() => {
    mountedRef.current = true
    loadData(
      pageIndex,
      pageSize,
      localSearchTerm,
      sortModel,
      initialFilters.current,
    )
    return () => {
      mountedRef.current = false
      abortController.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  // Watch for filter changes from drawer (after initial mount)
  useEffect(() => {
    if (!mountedRef.current || isInitialMount.current) return

    setPageIndex(0)
    loadData(
      0,
      pageSize,
      localSearchTerm,
      sortModel,
      localFilterHooks.selectedFilters,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilterHooks.selectedFilters])

  useEffect(() => {
    if (!mountedRef.current || isInitialMount.current) return

    setPageIndex(0)
    loadData(
      0,
      pageSize,
      localSearchTerm,
      sortModel,
      localFilterHooks.selectedFilters,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearchTerm])

  useEffect(() => {
    if (!mountedRef.current || isInitialMount.current) return

    loadData(
      pageIndex,
      pageSize,
      localSearchTerm,
      sortModel,
      localFilterHooks.selectedFilters,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize])

  useEffect(() => {
    if (!mountedRef.current || isInitialMount.current) return

    setPageIndex(0)
    loadData(
      0,
      pageSize,
      localSearchTerm,
      sortModel,
      localFilterHooks.selectedFilters,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel])

  // Mark initial mount as complete after first render
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    }
  }, [])

  // Sync state to URL
  useEffect(() => {
    if (isInitialMount.current) {
      return
    }

    const params = new URLSearchParams()
    params.set('p', String(pageIndex + 1))
    params.set('size', String(pageSize))
    if (localSearchTerm) params.set('q', localSearchTerm)

    if (sortModel.length > 0) {
      params.set('sort', sortModel[0].field)
      params.set('order', sortModel[0].sort ?? 'asc')
    }

    for (const [groupId, options] of Object.entries(
      localFilterHooks.selectedFilters,
    )) {
      for (const optionKey of Object.keys(options)) {
        params.append(groupId, optionKey)
      }
    }

    setSearchParams(params, { replace: true })
  }, [
    pageIndex,
    pageSize,
    localSearchTerm,
    sortModel,
    localFilterHooks.selectedFilters,
    setSearchParams,
  ])

  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      const deselectedIds = selectedRows.filter(
        (id) => !newSelection.includes(id),
      )

      for (const deselectedId of deselectedIds) {
        updatesDispatch({
          action: 'unset',
          device: deselectedId,
        })
        userManuallySelected.current.delete(deselectedId)
      }

      const newlySelectedIds = newSelection.filter(
        (id) => !selectedRows.includes(id),
      )
      for (const selectedId of newlySelectedIds) {
        const device = deviceData.find((d) => d.uid === selectedId)
        if (
          skipAutoSelection.current.has(selectedId) ||
          userManuallySelected.current.has(selectedId)
        ) {
          return
        }

        if (device && device.availableUpdates?.length > 0) {
          const existingSelections = updatesState[selectedId]
          const hasExistingValidSelections =
            existingSelections &&
            Array.isArray(existingSelections) &&
            existingSelections.some((id) => id !== '')

          if (!hasExistingValidSelections) {
            const selectableUpdates = device.availableUpdates.filter(
              (update) => update.selectable,
            )
            if (selectableUpdates.length > 0) {
              const updatesByType = groupBy(
                selectableUpdates,
                (update) => update.type,
              )
              // Get types in alphabetical order
              const sortedTypes = Object.keys(updatesByType).sort((a, b) =>
                a.localeCompare(b),
              )
              // Get the first type alphabetically
              const firstType = sortedTypes[0]
              // Get updates for the first type
              const firstTypeUpdates = updatesByType[firstType]
              // Sort by semantic version (lowest to highest)
              const sortedUpdates = [...firstTypeUpdates].sort((a, b) =>
                semver.gt(a.version, b.version) ? 1 : -1,
              )
              // Get the highest version (last item after sorting low to high)
              const highestVersionUpdate = sortedUpdates.at(-1)
              // Dispatch the selected update
              updatesDispatch({
                action: 'set',
                updates: [highestVersionUpdate.id],
                device: device.uid,
              })
            }
          }
        }
      }
      setSelectedRows(newSelection)
    },
    [selectedRows, deviceData, updatesState, updatesDispatch],
  )

  const handleRowSelect = useCallback(
    (deviceId: string | number) => {
      if (!selectedRows.includes(deviceId)) {
        skipAutoSelection.current.add(deviceId)
        const newSelection = [...selectedRows, deviceId]
        setSelectedRows(newSelection)
        setTimeout(() => {
          skipAutoSelection.current.delete(deviceId)
        }, 200)
      }
    },
    [selectedRows],
  )

  const handleRowDeselect = useCallback(
    (deviceId: string | number) => {
      userManuallySelected.current.delete(deviceId)
      const newSelection = selectedRows.filter((id) => id !== deviceId)
      setSelectedRows(newSelection)
    },
    [selectedRows],
  )

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'deviceType',
        headerName: t('di:device.type'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => {
          const deviceTypes = types as Record<string, string>
          const deviceType = deviceTypes[params.value] ?? params.value
          const deviceRoleName = params.row.deviceRoleName

          if (deviceRoleName) {
            return (
              <Tooltip title={deviceRoleName} placement="top" arrow>
                <Box
                  component="span"
                  sx={{ display: 'inline-block', width: '100%' }}
                >
                  {deviceType}
                </Box>
              </Tooltip>
            )
          }

          return deviceType
        },
        flex: 1,
        minWidth: 80,
        sortable: true,
      },
      {
        field: 'isOnline',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 1,
        minWidth: 80,
        sortable: true,
      },
      {
        field: 'country',
        headerName: t('di:fota.country'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 50,
        sortable: true,
      },
      {
        field: 'availableUpdates',
        headerName: t('di:fota.updatesAvailable'),
        renderHeader: renderWrappedHeader,
        flex: 2,
        minWidth: 100,
        sortable: true,
        renderCell: (params) => (
          <MemoizedUpdateSelect
            value={params.row}
            updatesState={updatesState}
            updatesDispatch={updatesDispatch}
            userManuallySelected={userManuallySelected}
            onRowSelect={handleRowSelect}
            onRowDeselect={handleRowDeselect}
            componentId={params.row.uid}
          />
        ),
      },
    ],
    [t, updatesState, updatesDispatch, handleRowSelect, handleRowDeselect],
  )

  const isRowSelectable = useCallback((params) => {
    return params.row.availableUpdates.some((update) => update.selectable)
  }, [])

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      setPageIndex(params.page)
      setPageSize(params.pageSize)
    },
    [],
  )

  // Update URL immediately after debounce
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately for UI
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL and page reset
      searchDebounceTimer.current = setTimeout(() => {
        // Update URL params immediately
        const params = new URLSearchParams(searchParams)
        params.set('p', '1') // Reset to page 1
        if (newSearchTerm) {
          params.set('q', newSearchTerm)
        } else {
          params.delete('q')
        }
        setSearchParams(params, { replace: true })

        // Reset page index (will trigger data load)
        setPageIndex(0)
      }, 300)
    },
    [searchParams, setSearchParams],
  )

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentValue =
        localFilterHooks.selectedFilters[groupId]?.[optionKey] || ''
      localFilterHooks.handleFilterChange(
        groupId,
        optionKey,
        currentValue,
        false,
      )
    },
    [localFilterHooks],
  )

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model)
  }, [])

  return (
    <>
      {error && (
        <Alert
          data-testid="select-updates-error-alert"
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.25',
          },
        }}
      >
        <DataGrid
          data-testid="device-grid-select-updates"
          rowHeight={50}
          rows={deviceData}
          getRowId={(row) => row.uid}
          columns={columns}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          loading={isLoading}
          rowCount={countOfAvailableUpdates}
          paginationModel={{ page: pageIndex, pageSize: pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          checkboxSelection
          rowSelectionModel={selectedRows}
          isRowSelectable={isRowSelectable}
          keepNonExistentRowsSelected={true}
          onRowSelectionModelChange={handleSelectionModelChange}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onFilterClick: localFilterHooks.openDrawer,
              activeFilterCount: localFilterHooks.getActiveFilterCount(),
              enableFilters: true,
              enableSearch: true,
              selectedFilters: localFilterHooks.selectedFilters,
              filterConfig: filterConfig,
              onRemoveFilter: handleRemoveFilter,
              searchValue: localSearchTerm,
              onSearchChange: handleSearchChange,
            } as CustomToolbarProps,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          disableColumnFilter={true}
          disableColumnMenu={true}
          disableDensitySelector={true}
          disableColumnSelector={true}
          localeText={{
            noRowsLabel: 'No devices found',
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {filterConfig && (
        <FilterDrawer
          open={localFilterHooks.isDrawerOpen}
          onClose={localFilterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={localFilterHooks.selectedFilters}
          onFilterChange={localFilterHooks.handleFilterChange}
          onClearAll={localFilterHooks.clearAllFilters}
        />
      )}

      <Box
        id={'download button'}
        sx={{
          backgroundColor: '#eee',
          paddingTop: '24px',
          borderBottom: 'none !important',
          paddingBottom: '24px',
        }}
      >
        <Tooltip title={t('device.fotaJobs.startDownload')} placement="top">
          <Box
            component="span"
            sx={{
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <Button
              data-testid="start-download-button"
              variant="contained"
              color="success"
              onClick={() => setOpen(true)}
              disabled={Object.keys(updatesState).length === 0}
              sx={{
                border: 'none',
                boxShadow: 'none',
                padding: '8px 24px',
              }}
            >
              {t('device.fotaJobs.startDownload')}
            </Button>
          </Box>
        </Tooltip>
      </Box>

      <ConfirmDialog
        open={open}
        setOpen={setOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      >
        <Typography variant="h5" gutterBottom>
          {t('device.fotaJobs.confirmFotaDownload')}
        </Typography>
      </ConfirmDialog>
    </>
  )
}

export default SelectUpdatesStep
